import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { LifeBuoy, PackageCheck, Plus, ReceiptText, XCircle } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { EmptyState } from '../../../components/EmptyState';
import { useAssetStore, type PurchaseOrder } from '../../../store/assetStore';
import { useAssetCatalogStore } from '../../../store/assetCatalogStore';

const fieldClass =
  'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30';

const STATUS_BADGE: Record<PurchaseOrder['status'], 'muted' | 'info' | 'success' | 'destructive'> = {
  draft: 'muted', ordered: 'info', received: 'success', cancelled: 'destructive',
};

const money = (n: number, currency = 'INR') => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n); }
  catch { return `${currency} ${n.toLocaleString()}`; }
};

const EMPTY_FORM = { supplier_id: '', item_description: '', category: '', quantity: '1', unit_cost: '', expected_date: '', notes: '' };

/**
 * Purchase orders — assets.purchase_orders. Receiving an order registers one
 * In Stock asset per unit (with PO number, vendor and unit cost).
 */
export function PurchaseOrdersView() {
  const { purchaseOrders, fetchPurchaseOrders, createPurchaseOrder, receivePurchaseOrder, updatePurchaseOrder } = useAssetStore();
  const { vendors, categories, fetchVendors, fetchCategories } = useAssetCatalogStore();
  const [form, setForm] = useState(EMPTY_FORM);
  const [nextNumberHint, setNextNumberHint] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const { nextPoNumber } = useAssetStore();

  useEffect(() => {
    fetchPurchaseOrders().finally(() => setLoaded(true));
    fetchVendors();
    fetchCategories();
    nextPoNumber().then(setNextNumberHint).catch(() => setNextNumberHint(''));
  }, [fetchPurchaseOrders, fetchVendors, fetchCategories, nextPoNumber]);

  const quantity = Number(form.quantity) || 0;
  const unit = Number(form.unit_cost) || 0;

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.item_description.trim()) { toast.error('Describe what is being ordered'); return; }
    if (!Number.isInteger(quantity) || quantity < 1) { toast.error('Quantity must be at least 1'); return; }
    setSaving(true);
    try {
      const created = await createPurchaseOrder({
        supplier_id: form.supplier_id ? Number(form.supplier_id) : null,
        item_description: form.item_description,
        category: form.category || null,
        quantity,
        unit_cost: unit,
        expected_date: form.expected_date || null,
        notes: form.notes || null,
      });
      if (created?.ticketNo) {
        toast.success(`Purchase order created & linked to ticket ${created.ticketNo}`);
      } else {
        toast.success('Purchase order created');
      }
      setForm(EMPTY_FORM);
      nextPoNumber().then(setNextNumberHint).catch(() => undefined);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const receive = async (po: PurchaseOrder) => {
    setBusyId(po.id);
    try {
      const n = await receivePurchaseOrder(po.id);
      toast.success(`${po.poNumber} received — ${n} asset${n === 1 ? '' : 's'} added to stock`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (po: PurchaseOrder) => {
    setBusyId(po.id);
    try {
      await updatePurchaseOrder(po.id, { status: 'cancelled' });
      toast.success(`${po.poNumber} cancelled`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader className="mb-0" icon={ReceiptText} title="Purchase orders" description="Order hardware from vendors; receiving an order adds the items to stock." />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-4">
          <h2 className="mb-1 font-display text-card-title text-foreground">New purchase order</h2>
          <p className="mb-3 text-xs text-muted-foreground">{nextNumberHint ? `Will be numbered ${nextNumberHint}` : ' '}</p>
          <form className="space-y-3" onSubmit={create}>
            <select className={fieldClass} value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
              <option value="">Vendor (optional)</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <input className={fieldClass} placeholder="Item, e.g. Dell Latitude 7440" value={form.item_description} maxLength={500}
              onChange={(e) => setForm({ ...form, item_description: e.target.value })} />
            <select className={fieldClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="">Category (optional)</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <input className={fieldClass} type="number" min={1} max={1000} placeholder="Quantity" value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              <input className={fieldClass} type="number" min={0} step="0.01" placeholder="Unit cost" value={form.unit_cost}
                onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
            </div>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Expected delivery</span>
              <input className={fieldClass} type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} />
            </label>
            <p className="text-sm text-muted-foreground">Total: <span className="font-semibold text-foreground">{money(quantity * unit)}</span></p>
            <Button type="submit" className="w-full" isLoading={saving}><Plus /> Create order</Button>
          </form>
        </Card>

        <Card className="lg:col-span-2">
          <div className="border-b border-border px-4 py-3">
            <h2 className="flex items-center gap-2 font-display text-card-title text-foreground"><ReceiptText className="size-4" /> Orders ({purchaseOrders.length})</h2>
          </div>
          {purchaseOrders.length === 0 ? (
            <EmptyState className="m-4 border-0" title={loaded ? 'No purchase orders yet' : 'Loading…'} description="Create an order to track hardware on its way in." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 font-semibold">PO</th>
                    <th className="px-4 py-2 font-semibold">Item</th>
                    <th className="px-4 py-2 font-semibold">Vendor</th>
                    <th className="px-4 py-2 text-right font-semibold">Qty</th>
                    <th className="px-4 py-2 text-right font-semibold">Total</th>
                    <th className="px-4 py-2 font-semibold">Status</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {purchaseOrders.map((po) => (
                    <tr key={po.id} className="hover:bg-secondary/30">
                      <td className="px-4 py-2">
                        <p className="font-medium text-foreground">{po.poNumber}</p>
                        <p className="text-xs text-muted-foreground">{new Date(po.orderDate).toLocaleDateString()}</p>
                        {(po.ticketNo || po.ticketId) && (
                          <div className="mt-1 flex items-center gap-1">
                            <Link
                              to={`/support/tickets?ticketId=${encodeURIComponent(po.ticketNo || String(po.ticketId))}`}
                              className="inline-flex items-center gap-1 rounded bg-info/10 px-1.5 py-0.5 text-[11px] font-medium text-info hover:bg-info/20 hover:underline"
                              title="View linked Support Request Ticket"
                            >
                              <LifeBuoy className="size-3" />
                              {po.ticketNo || `Ticket #${po.ticketId}`}
                            </Link>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-foreground">
                        {po.itemDescription}
                        {po.category && <p className="text-xs text-muted-foreground">{po.category}</p>}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">{po.vendorName || '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-foreground">{po.quantity}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-foreground">{money(po.totalCost, po.currency)}</td>
                      <td className="px-4 py-2">
                        <Badge variant={STATUS_BADGE[po.status]} className="capitalize">{po.status}</Badge>
                        {po.status === 'received' && <p className="mt-0.5 text-xs text-muted-foreground">{po.receivedAssets} in register</p>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        {po.status === 'ordered' && (
                          <div className="flex justify-end gap-1">
                            <Button size="xs" onClick={() => receive(po)} isLoading={busyId === po.id}><PackageCheck /> Receive</Button>
                            <Button size="xs" variant="ghost" onClick={() => cancel(po)} disabled={busyId === po.id} aria-label={`Cancel ${po.poNumber}`}><XCircle /></Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
